// src/views/about-us.js
import React from 'react'
import { Helmet } from 'react-helmet'
import { Link } from 'react-router-dom'
import Navigation from '../components/navigation'
import Footer from '../components/footer'
import './about-us.css'

const AboutUs = () => {
  React.useEffect(() => {
    // Scroll to top on mount
    window.scrollTo(0, 0)

    // Intersection observer for fade-in animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-up')
          }
        })
      },
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
    )

    const sections = document.querySelectorAll('.observe-section')
    sections.forEach((section) => observer.observe(section))

    return () => observer.disconnect()
  }, [])

  React.useEffect(() => {
    const handleMobileVideo = () => {
      const video = document.querySelector('.about-hero-image');
      if (video && window.innerWidth <= 768) {
        // Set mobile-specific attributes
        video.setAttribute('playsinline', 'true');
        video.setAttribute('webkit-playsinline', 'true');
        video.muted = true;
        video.autoplay = true;
        
        // Attempt to play
        const playPromise = video.play();
        
        if (playPromise !== undefined) {
          playPromise.catch(err => {
            console.log('Video autoplay failed:', err);
            // Fallback: try playing on user interaction
            const playOnTouch = () => {
              video.play().catch(() => {});
              document.removeEventListener('touchstart', playOnTouch);
            };
            document.addEventListener('touchstart', playOnTouch, { once: true });
          });
        }
      }
    };
    
    // Run on mount and window resize
    handleMobileVideo();
    window.addEventListener('resize', handleMobileVideo);
    
    // Also try playing when video metadata is loaded
    const video = document.querySelector('.about-hero-image');
    if (video) {
      video.addEventListener('loadedmetadata', handleMobileVideo);
    }
    
    return () => {
      window.removeEventListener('resize', handleMobileVideo);
      if (video) {
        video.removeEventListener('loadedmetadata', handleMobileVideo);
      }
    };
  }, []);

  return (
    <div className="about-page-container">
      <Helmet>
        <title>About Nature Ready | Our Story & Vision</title>
        <meta
          property="og:title"
          content="About Nature Ready | Our Story & Vision"
        />
        <meta
          name="description"
          content="Learn about Nature Ready's origins, mission, core values, and global impact. Discover how we bring India's agricultural excellence to the world."
        />
      </Helmet>

      <Navigation />

      {/* Hero Section */}
      <section className="about-hero-section">
        <div className="about-hero-background">
          <video
            src={require('../images/au_hero2.mp4')}
            className="about-hero-image"
            autoPlay
            loop
            muted
            playsInline
            webkit-playsinline="true"
            preload="auto"
            poster={require('../images/hero_au.jpg')}
          />

          <div className="about-hero-overlay"></div>
        </div>
        <div className="about-hero-content">
          <div className="about-hero-badge">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M12 2L2 7l10 5 10-5-10-5z" />
              <path d="M2 17l10 5 10-5M2 12l10 5 10-5" />
            </svg>
            <span>Our Story</span>
          </div>
          <h1 className="about-hero-title">About Nature Ready</h1>
          <p className="about-hero-subtitle">
            From India's fertile heartlands to global shelves — where tradition
            meets traceability.
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="our-story-section observe-section">
        <div className="section-container">
          <div className="story-grid">
            <div className="story-content">
              <h2 className="section-title">Our Story</h2>
              <p className="section-content">
                Nature Ready was founded with a singular vision — to make India's
                rich agricultural heritage accessible to the world through
                ethical, transparent, and sustainable export practices. What
                began as a small collaboration with certified growers has evolved
                into a trusted global export brand delivering premium-quality
                botanicals, spices, and agrarian produce across continents.
              </p>
              <p className="section-content">
                Each product we export carries with it the care of our farmers,
                the integrity of our processes, and the assurance of
                international compliance. We believe global trade should be as
                pure as the produce itself — honest, traceable, and responsibly
                managed.
              </p>
            </div>
            <div className="story-visual">
              <img
                src={require('../images/story_au.jpg')}
                alt="Nature Ready leadership team"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="mission-section observe-section">
        <div className="section-container">
          <div className="mission-grid">
            <div className="mission-card">
              <div className="mission-icon">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 6v6l4 2" />
                </svg>
              </div>
              <h3>Our Mission</h3>
              <p>
                To deliver India's finest agricultural and botanical produce to
                discerning international markets — ethically sourced, expertly
                packed, and globally certified.
              </p>
            </div>
            <div className="mission-card">
              <div className="mission-icon">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              </div>
              <h3>Our Vision</h3>
              <p>
                To become the benchmark for responsible export excellence — where
                sustainability, quality, and transparency define every shipment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="values-section observe-section">
        <div className="section-container">
          <h2 className="section-title">Our Core Values</h2>
          <p className="section-subtitle">
            The principles that guide every decision we make
          </p>
          <div className="values-grid">
            <div className="value-item">
              <div className="value-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 2l7 4v5c0 5-3.5 9-7 11-3.5-2-7-6-7-11V6l7-4z" />
                  <path d="M9 12l2 2 4-4" />
                </svg>

              </div>
              <h3>Integrity</h3>
              <p>We operate with full transparency — from soil to shipment.</p>
            </div>
            <div className="value-item">
              <div className="value-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
                </svg>
              </div>
              <h3>Sustainability</h3>
              <p>
                Eco-conscious sourcing and recyclable packaging define our
                process.
              </p>
            </div>
            <div className="value-item">
              <div className="value-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <h3>Quality</h3>
              <p>
                Every batch is lab-tested, certified, and meets international
                export standards.
              </p>
            </div>
            <div className="value-item">
              <div className="value-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <h3>Community</h3>
              <p>
                We support Indian farmers and cooperatives through fair-trade
                partnerships and continuous reinvestment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="leadership-section observe-section">
        <div className="section-container">
          <div className="leadership-grid">
            <div className="leadership-image">
              <img
                src={require('../images/people_au.jpg')}
                alt="Farmers sorting organic produce"
              />
            </div>
            <div className="leadership-content">
              <h2 className="section-title">Leadership & People</h2>
              <p className="section-content">
                Behind every Nature Ready product stands a dedicated team of
                agronomists, logistics specialists, quality inspectors, and global
                trade professionals. Together, we ensure our partners and
                customers receive consistent quality, reliable delivery, and
                transparent documentation — every single time.
              </p>
              <p className="section-content">
                Our leadership believes in collaboration over competition, value
                over volume, and impact over intent. It's this philosophy that
                drives us to be a trusted partner across 25+ global distribution
                hubs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Global Impact */}
      <section className="impact-section observe-section">
        <div className="section-container">
          <h2 className="section-title">Global Impact</h2>
          <p className="section-subtitle">
            Strengthening India's position as a source of premium, ethically
            traded produce
          </p>
          <p className="section-content impact-description">
            With exports spanning Europe, North America, the Middle East, and East
            Asia, Nature Ready continues to strengthen India's position as a
            global source of premium, ethically traded produce. Our impact extends
            beyond trade — fostering rural livelihoods, preserving agricultural
            biodiversity, and ensuring sustainable growth for the communities we
            serve.
          </p>
          <div className="impact-stats">
            <div className="stat-item">
              <h3>25+</h3>
              <p>Distribution hubs worldwide</p>
            </div>
            <div className="stat-item">
              <h3>4.8/5</h3>
              <p>Global partner satisfaction</p>
            </div>
            <div className="stat-item">
              <h3>100%</h3>
              <p>Traceable supply chain</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="about-cta-section observe-section">
        <div className="section-container">
          <div className="about-cta-content">
            <h2 className="cta-title">Partner With Nature Ready</h2>
            <p className="cta-text">
              Join our growing network of global distributors, importers, and
              ethical trade partners who trust our quality and consistency.
            </p>
            <Link to="/inquiry-custom-order" className="btn btn-primary btn-lg">
              Start Your Inquiry
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default AboutUs
